import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  CustomButton,
  CustomChip,
  CustomCircularProgress,
  CustomDrawer,
  DateInput,
} from '@components';
import {
  BillingInterval,
  BillingModel,
  ClientOrganization,
  EntitlementType,
  OrganizationTierCategory,
  SubscriptionStatus,
  TierCodes,
  UpdateOrganizationBillingInput,
} from '@graphql';
import BorderColorOutlined from '@mui/icons-material/BorderColorOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  TextField,
  SelectChangeEvent,
  Tooltip,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { appContext, capitalizeFirstLetter, delay } from '@utils';
import { FormikHelpers, useFormik } from 'formik';
import { COLORS } from '@styles';
import { observer } from 'mobx-react';
import { toast } from 'react-toastify';
import { RequestStatus } from '@store';
import dayjs from 'dayjs';
import { compareTiers, formatDate, getBillingCategory } from '../../utils';
import {
  BillingModelLabels,
  IManagePlansAndSeatsFormikValues,
  StripeSubscriptionStatus,
} from './types';
import ChangePlanConfirmationModal from './components/ChangePlanModal';
import { makeValidationSchema } from './helpers';

interface IProps {
  organization?: ClientOrganization;
  fetchOrganizationBilling?: boolean;
  open: boolean;
  onClose: () => void;
}

const makeStyles = () => ({
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
  } as React.CSSProperties,
  editHeader: {
    paddingBottom: '1rem',
    borderBottom: `1px dashed ${COLORS.NEUTRAL_LIGHT_3}`,
  },
  dashedDivider: {
    width: '100%',
  },
  formContainer: {
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
    padding: '0 1rem',
  } as React.CSSProperties,
  headerClientId: {
    color: COLORS.NEUTRAL_LIGHT_0,
  },
  formItems: {
    margin: '1rem 0',
    borderRadius: '1rem',
  },
  inputBorder: {
    borderRadius: '1rem',
  },
  disabledMenuItem: {
    pointerEvents: 'auto !important',
    cursor: 'not-allowed !important',
  },
  section: {
    margin: '1.5rem 0 0.5rem 0',
  },
  employeeSeatsSection: {
    margin: '1.5rem 0 0.5rem 0',
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  button: {
    padding: '1.5rem 1rem 0 1rem',
    borderTop: `1px solid ${COLORS.NEUTRAL_DARK_2}`,
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  } as React.CSSProperties,
  fullsizeSpan: { width: '100%' },
  checkbox: {
    '&.Mui-checked': {
      color: COLORS.ELEKTRAFI_NEUTRAL,
    },
  },
  checkboxLabel: {
    margin: '0.5rem 0 0 1rem',
  },
  employeeSeatsSectionWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0.75rem 0',
    gap: '1rem',
  },
  employeeSeatsSectionContent: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  } as React.CSSProperties,
  employeeSeatsSectionCountingDetails: {
    display: 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,
  employeeSeatsSectionPercentageLabel: {
    fontSize: '0.75rem',
    fontWeight: 400,
  } as React.CSSProperties,
  employeeSeatsSectionPercentageValue: {
    fontSize: '1.25rem',
    fontWeight: 600,
  } as React.CSSProperties,
});

function EmployeeSeatsSection({
  purchasedSeats,
  usedSeats,
}: {
  purchasedSeats: number;
  usedSeats: number;
}) {
  const sx = makeStyles();
  const usagePercentage = purchasedSeats > 0 ? (usedSeats / purchasedSeats) * 100 : 0;

  const percentageLabel = `${usagePercentage?.toFixed(0)}%`;

  return (
    <div style={sx.employeeSeatsSectionWrapper}>
      <div style={sx.employeeSeatsSectionContent}>
        <CustomCircularProgress size={35} variant="determinate" value={75} />
        <div style={sx.employeeSeatsSectionCountingDetails}>
          <Typography sx={sx.employeeSeatsSectionPercentageLabel} variant="subtitle2">
            Seats Usage
          </Typography>
          <Typography sx={sx.employeeSeatsSectionPercentageValue} variant="subtitle2">
            {`${usedSeats} of ${purchasedSeats} seats`}
          </Typography>
        </div>
      </div>
      <CustomChip label={percentageLabel} variant="purpleGradient" />
    </div>
  );
}

export const ManagePlansAndSeats = observer(
  ({ organization, open, onClose, fetchOrganizationBilling }: IProps) => {
    const sx = makeStyles();

    const {
      tiersStore: { tiers, getTierAddons, getTierNameById },
      billingStore: {
        organizationBillingData,
        getOrganizationBilling,
        upgradeOrganizationPlan,
        downgradeOrganizationPlan,
        getInvoices,
        saveOrganizationBillingDetails,
      },
      organizationStore: { refreshOrganizationViews },
    } = useContext(appContext);

    const currentBillingModel = organizationBillingData?.data?.billingModel;
    const currentSubscriptionStatus = organizationBillingData?.data?.status;
    const isSavingBillingForCreditCardCustomer = currentBillingModel === BillingModel.CREDIT_CARD;
    const isDowngrading = currentSubscriptionStatus === SubscriptionStatus.Downgrading;
    const isDemoAccount = !organization?.isPayingCustomer;

    const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);

    useEffect(() => {
      if (open && fetchOrganizationBilling) {
        getOrganizationBilling(organization?.id || '');
      }
    }, [organization?.id, getOrganizationBilling, open, fetchOrganizationBilling]);

    const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
    const [initialEnabledAddons, setInitialEnabledAddons] = useState<string[]>([]);

    // Check if organization has active credit card subscription
    const hasActiveCreditCardSubscription = useMemo(
      () =>
        currentBillingModel === BillingModel.CREDIT_CARD &&
        (currentSubscriptionStatus === SubscriptionStatus.Active ||
          currentSubscriptionStatus === SubscriptionStatus.Trialing),
      [currentBillingModel, currentSubscriptionStatus],
    );

    // Check if organization has invoice billing model
    const hasInvoiceBillingModel = useMemo(
      () => currentBillingModel === BillingModel.INVOICE,
      [currentBillingModel],
    );

    // Check if organization has an active subscription
    const hasActiveSubscription = useMemo(
      () => currentSubscriptionStatus !== SubscriptionStatus.Pending,
      [currentSubscriptionStatus],
    );

    // Helper to check if an add-on is existing (was enabled initially)
    const isExistingAddon = useMemo(
      () => (addonFeatureFlagKey: string) => initialEnabledAddons.includes(addonFeatureFlagKey),
      [initialEnabledAddons],
    );

    useEffect(() => {
      if (organization?.organizationFeatures && open) {
        const addons = organization?.organizationFeatures
          ?.filter(
            (feature) =>
              feature?.type === EntitlementType.ADD_ON &&
              feature?.enabledAt &&
              !feature?.disabledAt,
          )
          ?.map((feature) => feature?.feature?.featureFlagKey);

        setSelectedAddons(addons || []);
        setInitialEnabledAddons(addons || []);
      }
    }, [organization?.organizationFeatures, open, setSelectedAddons]);

    const handleAddonChange = (addon: string, checked: boolean) => {
      // Prevent unchecking existing add-ons for credit card subscriptions
      if (!checked && hasActiveCreditCardSubscription && isExistingAddon(addon)) {
        return;
      }

      if (checked) {
        setSelectedAddons([...selectedAddons, addon]);
      } else {
        setSelectedAddons(selectedAddons.filter((selectedAddon) => selectedAddon !== addon));
      }
    };

    const calculateEndDate = useCallback(() => {
      const startDate = organizationBillingData?.data?.startedAt;
      if (!startDate) return null;
      return dayjs(startDate).add(1, 'year').toISOString();
    }, [organizationBillingData?.data?.startedAt]);

    const initialValues = useMemo<IManagePlansAndSeatsFormikValues>(
      () => ({
        id: organization?.id || '',
        currentTierId: organization?.currentTier?.id || '',
        purchasedSeats: organizationBillingData?.data?.purchasedSeats || 0,
        billingInterval: organizationBillingData?.data?.billingInterval || BillingInterval.monthly,
        billingModel: organizationBillingData?.data?.billingModel || BillingModel.INVOICE,
        startDate: organizationBillingData?.data?.startedAt || null,
        endDate: organizationBillingData?.data?.endsAt || calculateEndDate(),
        trialPeriodDays: organizationBillingData?.data?.trialPeriodDays ?? null,
      }),
      [organizationBillingData?.data, organization, calculateEndDate],
    );

    const handleDowngradeOrganizationPlan = async (payload: {
      orgId: string;
      targetTierId: string;
      billingInterval: BillingInterval;
    }): Promise<boolean | null> => downgradeOrganizationPlan(payload);

    const handleUpgradeOrganizationPlan = async (
      values: IManagePlansAndSeatsFormikValues,
    ): Promise<boolean | null> => {
      if (!organization?.id) {
        return null;
      }

      // For credit card subscriptions, convert feature flag keys to feature UUIDs
      // For invoice billing, keep using feature flag keys
      let addonsPayload: string[] = selectedAddons;
      if (hasActiveCreditCardSubscription) {
        const currentTierAddons = getTierAddons(values.currentTierId);
        addonsPayload = selectedAddons
          .map((featureFlagKey) => {
            // First try to find in organizationFeatures (for existing add-ons)
            const orgFeature = organization?.organizationFeatures?.find(
              (feature) =>
                feature?.type === EntitlementType.ADD_ON &&
                feature?.feature?.featureFlagKey === featureFlagKey,
            );
            if (orgFeature?.feature?.id) {
              return orgFeature.feature.id;
            }

            // Fallback to tierAddons (for new add-ons not yet in organizationFeatures)
            const tierAddon = currentTierAddons.find(
              (addon) => addon.feature.featureFlagKey === featureFlagKey,
            );
            if (tierAddon?.feature?.id) {
              return tierAddon.feature.id;
            }

            // If not found, return the feature flag key as fallback
            return featureFlagKey;
          })
          .filter((id) => id !== undefined) as string[];
      }

      const payload = {
        orgId: organization?.id,
        targetTierId: values.currentTierId,
        billingInterval: values.billingInterval,
        additionalSeats: values.purchasedSeats - initialValues.purchasedSeats,
        addons: addonsPayload,
      };

      return upgradeOrganizationPlan(payload);
    };

    const handleUpdateOrganizationPlan = async (
      values: IManagePlansAndSeatsFormikValues,
    ): Promise<boolean | null> => {
      if (!organization?.id) {
        return null;
      }

      const initialTierCode = organization?.currentTier?.code as TierCodes;
      const targetTierCode = tiers.find((tier) => tier?.id === values.currentTierId)
        ?.code as TierCodes;

      const currentStripeSubscriptionStatus =
        organizationBillingData?.data?.subscriptionDetails?.status;

      // Stripe blocks subscription update if status is 'incomplete'
      if (currentStripeSubscriptionStatus === StripeSubscriptionStatus.incomplete) {
        toast(
          'Subscription is incomplete. Please ask the customer to add a payment method to continue.',
          { type: 'error' },
        );
        return null;
      }

      // Stripe considers downgrading any change that results in lower revenue
      const isSeatsDecreasing =
        organizationBillingData?.data?.purchasedSeats &&
        organizationBillingData?.data?.purchasedSeats > values.purchasedSeats;
      const isChangingBillingModelToMonthly =
        organizationBillingData?.data?.billingInterval === BillingInterval.yearly &&
        values.billingInterval === BillingInterval.monthly;

      if (
        compareTiers(initialTierCode, targetTierCode) < 0 ||
        isSeatsDecreasing ||
        isChangingBillingModelToMonthly
      ) {
        return handleDowngradeOrganizationPlan({
          orgId: organization?.id,
          targetTierId: values.currentTierId,
          billingInterval: values.billingInterval,
        });
      }

      return handleUpgradeOrganizationPlan(values);
    };

    const seatsUsage = useMemo(
      () => ({
        purchasedSeats: organizationBillingData?.data?.purchasedSeats || 0,
        usedSeats: organization?.clientsCount || 0,
      }),
      [organizationBillingData?.data?.purchasedSeats, organization?.clientsCount],
    );

    const handleSaveOrganizationBillingDetails = async (
      orgId: string,
      values: IManagePlansAndSeatsFormikValues,
    ) => {
      const payload: UpdateOrganizationBillingInput = {
        tierId: values.currentTierId,
        billingInterval: values.billingInterval,
        billingModel: values.billingModel,
        purchasedSeats: values.purchasedSeats,
        addons: selectedAddons,
        startsAt: values.startDate ? dayjs(values.startDate).toISOString() : undefined,
        endsAt: values.endDate ? dayjs(values.endDate).toISOString() : undefined,
        trialPeriodDays: values.trialPeriodDays ?? undefined,
      };

      const response = await saveOrganizationBillingDetails(orgId, payload);

      return response;
    };

    const handleFormSubmit = async (
      values: IManagePlansAndSeatsFormikValues,
      { setSubmitting }: FormikHelpers<IManagePlansAndSeatsFormikValues>,
    ) => {
      if (!organization?.id) {
        toast('Organization not found', { type: 'error' });
        setSubmitting(false);
        return;
      }

      const isTierChange = organization?.currentTier?.id !== values.currentTierId;
      const isChangingBillingModel = currentBillingModel !== values.billingModel;

      // Organization on Credit Card billing model and downgrading do not support updates
      if (isSavingBillingForCreditCardCustomer && isDowngrading) {
        toast('Organization on Credit Card billing model and downgrading do not support updates', {
          type: 'error',
        });
        setSubmitting(false);
        return;
      }

      // If it's a plan change, we need to show a confirmation modal
      if (isTierChange) {
        setIsChangePlanModalOpen(true);
        return;
      }

      let result = null;

      // Handle Subscription changes (downgrade, upgrade, etc.)
      if (isSavingBillingForCreditCardCustomer && !isChangingBillingModel && hasActiveSubscription) {
        result = await handleUpdateOrganizationPlan(values);
      }
      // Otherwise, handle billing details changes
      else {
        result = await handleSaveOrganizationBillingDetails(organization?.id, values);
      }

      if (result) {
        await delay(2000);
        toast('Organization plan updated successfully', { type: 'success' });
        await Promise.all([
          refreshOrganizationViews(organization.id),
          getOrganizationBilling(organization.id),
          getInvoices(organization.id),
        ]);
        onClose();
      }
    };

    const formik = useFormik<IManagePlansAndSeatsFormikValues>({
      initialValues,
      validationSchema: makeValidationSchema(seatsUsage.usedSeats ?? 1),
      onSubmit: handleFormSubmit,
      enableReinitialize: true,
      validateOnChange: false,
    });

    const tierSelectOptions = useMemo(
      () =>
        tiers?.map((tier) => ({
          label: tier?.name,
          value: tier?.id,
          disabled:
            getBillingCategory(tier?.code as TierCodes) === OrganizationTierCategory.Enterprise &&
            formik.values.billingModel === BillingModel.CREDIT_CARD,
        })),
      [tiers, formik.values.billingModel],
    );

    const tierAddons = useMemo(
      () => getTierAddons(formik.values.currentTierId),
      [formik.values.currentTierId, getTierAddons],
    );

    const billingIntervalOptions = useMemo(
      () =>
        Object.values(BillingInterval).map((interval) => ({
          label: capitalizeFirstLetter(interval),
          value: interval,
        })),
      [],
    );

    const isOrganizationPlanDisabled = useMemo(() => {
      const isBlockedStatus = [
        SubscriptionStatus.Downgrading,
        SubscriptionStatus.CancelingAtPeriodEnd,
        SubscriptionStatus.PastDue,
      ].includes(organizationBillingData?.data?.status as SubscriptionStatus);

      return isBlockedStatus;
    }, [organizationBillingData?.data?.status]);

    const isDowngradingAction = useMemo(() => {
      const initialTierCode = organization?.currentTier?.code as TierCodes;
      const targetTierCode = tiers.find((tier) => tier?.id === formik.values.currentTierId)
        ?.code as TierCodes;

      return compareTiers(initialTierCode, targetTierCode) < 0;
    }, [organization?.currentTier?.code, formik.values.currentTierId, tiers]);

    const isDowngradeInProgress = useMemo(
      () => organizationBillingData?.data?.status === SubscriptionStatus.Downgrading,
      [organizationBillingData?.data?.status],
    );

    const disableSubmit = useMemo(
      () => organizationBillingData?.status === RequestStatus.LOADING || isDowngradeInProgress,
      [organizationBillingData?.status, isDowngradeInProgress],
    );

    const nextRenewalDate = useMemo(
      () => organizationBillingData?.data?.nextBillingDate || '',
      [organizationBillingData?.data?.nextBillingDate],
    );

    const handleChangePlanConfirmation = async () => {
      if (!organization?.id) {
        return;
      }

      let result = null;

      const isChangingBillingModel = currentBillingModel !== formik.values.billingModel;
      // Handle Subscription changes (downgrade, upgrade, etc.)
      if (isSavingBillingForCreditCardCustomer && hasActiveSubscription && !isChangingBillingModel) {
        result = await handleUpdateOrganizationPlan(formik.values);
      }
      // Otherwise, handle billing details changes
      else {
        result = await handleSaveOrganizationBillingDetails(organization?.id, formik.values);
      }

      if (result) {
        await delay(2000);
        toast('Organization plan updated successfully', { type: 'success' });

        const promises = [
          refreshOrganizationViews(organization.id),
          getOrganizationBilling(organization.id),
          getInvoices(organization.id),
        ];

        await Promise.all(promises);
        setIsChangePlanModalOpen(false);
        onClose();
      }
    };

    const handleBillingModelChange = (event: SelectChangeEvent<BillingModel>) => {
      const currentSelectedTier = tiers.find((tier) => tier?.id === formik.values.currentTierId);
      if (!currentSelectedTier) {
        toast('Tier not found while validating billing model change', { type: 'error' });
      }

      if (
        getBillingCategory(currentSelectedTier?.code as TierCodes) ===
          OrganizationTierCategory.Enterprise &&
        event.target.value === BillingModel.CREDIT_CARD
      ) {
        toast('Credit card billing isn’t supported for the Enterprise tier.', { type: 'error' });
        return;
      }

      if (event.target.value === BillingModel.INVOICE) {
        formik.setFieldValue('billingInterval', BillingInterval.yearly);
        formik.setFieldValue('trialPeriodDays', null);
      }

      formik.handleChange(event);
    };

    const isCreditCard = formik.values.billingModel === BillingModel.CREDIT_CARD;
    const isInvoice = formik.values.billingModel === BillingModel.INVOICE;

    return (
      <CustomDrawer
        open={open}
        onClose={onClose}
        headerLabel="Manage Plans and Seats"
        headerIcon={<BorderColorOutlined />}
      >
        <form style={sx.formWrapper} onSubmit={formik.handleSubmit}>
          <div style={sx.formContainer}>
            <Typography sx={sx.section} variant="subtitle2">
              Organization Plan Details
            </Typography>
            <FormControl fullWidth>
              <InputLabel sx={sx.formItems}>Organization Plan</InputLabel>
              <Select
                sx={sx.formItems}
                fullWidth
                name="currentTierId"
                onChange={formik.handleChange}
                value={formik.values.currentTierId}
                label="Organization Plan"
                disabled={isOrganizationPlanDisabled}
              >
                {tierSelectOptions.map(({ label, value, disabled }) => (
                  <MenuItem key={value} value={value} disabled={disabled}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {tierAddons.length > 0 && (
              <Typography sx={sx.section} variant="subtitle2">
                Platform Add-Ons
              </Typography>
            )}
            {tierAddons.map((addon) => {
              const addonFeatureFlagKey = addon.feature.featureFlagKey;
              const isChecked = selectedAddons.includes(addonFeatureFlagKey);
              const isExisting = isExistingAddon(addonFeatureFlagKey);
              const shouldDisable = hasInvoiceBillingModel
                ? false
                : isDowngradingAction ||
                  isDowngradeInProgress ||
                  (hasActiveCreditCardSubscription && isChecked && isExisting);

              const checkbox = (
                <Checkbox
                  name={`${addonFeatureFlagKey}`}
                  checked={isChecked}
                  onChange={(_, checked) => handleAddonChange(addonFeatureFlagKey, checked)}
                  sx={{
                    ...sx.checkbox,
                    '&.Mui-disabled': { color: COLORS.NEUTRAL_LIGHT_1, opacity: 0.3 },
                  }}
                  disabled={shouldDisable}
                />
              );

              return (
                <FormControlLabel
                  key={addonFeatureFlagKey}
                  sx={sx.checkboxLabel}
                  control={
                    hasActiveCreditCardSubscription && isChecked && isExisting ? (
                      <Tooltip title="Removal of add ons from active subscriptions is not supported yet. Please contact the development team if you need to change this subscription.">
                        <span>{checkbox}</span>
                      </Tooltip>
                    ) : (
                      checkbox
                    )
                  }
                  label={addon?.feature?.name}
                />
              );
            })}
            <Typography sx={sx.employeeSeatsSection} variant="subtitle2">
              Employee Seats
            </Typography>

            <EmployeeSeatsSection
              purchasedSeats={seatsUsage.purchasedSeats}
              usedSeats={seatsUsage.usedSeats}
            />

            <TextField
              style={sx.formItems}
              fullWidth
              label="N° of Seats"
              name="purchasedSeats"
              onChange={formik.handleChange}
              value={formik.values.purchasedSeats}
              variant="outlined"
              type="number"
              InputProps={{ style: sx.inputBorder }}
              error={!!formik.errors.purchasedSeats}
              helperText={formik.errors.purchasedSeats}
              placeholder="Seats"
              disabled={isDowngradingAction || isDowngradeInProgress}
            />

            <Typography sx={sx.section} variant="subtitle2">
              Organization Management
            </Typography>

            {/* Billing Model */}
            {hasInvoiceBillingModel ? (
              <>
                <FormControl fullWidth>
                  <InputLabel sx={sx.formItems}>Payment Method</InputLabel>
                  <Select
                    sx={sx.formItems}
                    fullWidth
                    name="billingModel"
                    value={BillingModel.INVOICE}
                    label="Payment Method"
                    inputProps={{
                      readOnly: true,
                    }}
                    disabled
                  >
                    <MenuItem value={BillingModel.INVOICE}>
                      {BillingModelLabels[BillingModel.INVOICE]}
                    </MenuItem>
                  </Select>
                </FormControl>
                <Alert severity="info" sx={{ display: 'flex', alignItems: 'center', margin: "0.5rem 0", borderRadius: '1rem'}}>
                  Switching from invoice to credit card billing is not supported.
                </Alert>
              </>
            ) : (
              <FormControl fullWidth>
                <InputLabel sx={sx.formItems}>Payment Method</InputLabel>
                <Select
                  sx={sx.formItems}
                  fullWidth
                  name="billingModel"
                  value={formik.values.billingModel}
                  onChange={handleBillingModelChange}
                  label="Payment Method"
                  disabled={isDemoAccount}
                >
                  {Object.values(BillingModel).map((billingModel) => (
                    <MenuItem key={billingModel} value={billingModel}>
                      {BillingModelLabels[billingModel]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Payment Frequency */}
            <FormControl fullWidth>
              <InputLabel sx={sx.formItems}>Payment Frequency</InputLabel>
              <Select
                sx={sx.formItems}
                fullWidth
                name="billingInterval"
                required
                onChange={formik.handleChange}
                value={formik.values.billingInterval}
                label="Billing Interval"
                disabled={isInvoice || isDowngradingAction || isDowngradeInProgress}
              >
                {billingIntervalOptions.map((interval) => (
                  <MenuItem key={interval.value} value={interval.value}>
                    {interval.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Trial Period Days */}
            <FormControl fullWidth>
              <TextField
                style={sx.formItems}
                fullWidth
                label="Trial Period Days (Optional)"
                name="trialPeriodDays"
                onChange={formik.handleChange}
                value={formik.values.trialPeriodDays ?? ''}
                variant="outlined"
                type="number"
                InputProps={{
                  style: sx.inputBorder,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="If not provided, defaults to 14 days">
                        <IconButton edge="end" size="small" sx={{ color: COLORS.NEUTRAL_LIGHT_3 }}>
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!formik.errors.trialPeriodDays}
                helperText={formik.errors.trialPeriodDays}
                placeholder="Trial Period Days"
                disabled={hasActiveSubscription || formik.values.billingModel === BillingModel.INVOICE}
              />
            </FormControl>

            {/* Dates row */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <FormControl fullWidth style={sx.formItems}>
                <DateInput
                  label="Starting Date"
                  name="startDate"
                  value={formik.values.startDate}
                  setFieldValue={formik.setFieldValue}
                  error={!!formik.errors.startDate}
                  disabled={isCreditCard}
                />
              </FormControl>

              <FormControl fullWidth style={sx.formItems}>
                <DateInput
                  label="Ending Date"
                  name="endDate"
                  value={formik.values.endDate}
                  setFieldValue={formik.setFieldValue}
                  error={!!formik.errors.endDate}
                  disabled={isCreditCard}
                />
              </FormControl>
            </div>
          </div>

          <div style={sx.button}>
            <CustomButton
              variant="default"
              type="submit"
              isLoading={formik.isSubmitting}
              disabled={disableSubmit}
            >
              Save Changes
            </CustomButton>
            {isDowngradeInProgress && (
              <Typography variant="subtitle2" fontSize="0.75rem">
                Downgrading in {formatDate(nextRenewalDate)}
              </Typography>
            )}
          </div>
        </form>

        <ChangePlanConfirmationModal
          isOpen={isChangePlanModalOpen}
          onClose={() => setIsChangePlanModalOpen(false)}
          organization={organization}
          organizationBilling={organizationBillingData?.data || undefined}
          formik={formik}
          getTierNameById={getTierNameById}
          onFormSubmit={handleChangePlanConfirmation}
        />
      </CustomDrawer>
    );
  },
);
